import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import { CardText } from "react-bootstrap-icons";
import { SemesterRecord, ThesisRecord, ThesisStatusRecord, ThesisTypeRecord } from "../generated/pocketbase";
import { HighlightedText } from "./HighlightedText";
type GroupedThesis = Record<string, ThesisRecord[]>

const groupByStatus = (thesis: ThesisRecord[]): GroupedThesis =>
  thesis.reduce<GroupedThesis>((acc, t) => {
    acc[t.status] = acc[t.status] ?? [];
    acc[t.status].push(t)
    return acc;
  }, {});

export const ThesisList = (p: {
  types: ThesisTypeRecord[];
  semester: SemesterRecord[];
  status: ThesisStatusRecord[];
  thesis: ThesisRecord[];
  search?: string;
  onClickThesis: (t: ThesisRecord) => void;
}) => {
  const groupedThesis = useMemo(() =>
    groupByStatus(p.thesis.filter(t =>
      !p.search || p.search.toLocaleLowerCase()
        .split(" ")
        .map(s => s.trim())
        .filter(s => !!s)
        .some(fragment =>
          `${t.name} ${t.topic} ${t.relatedProject} ${t.supervisorName} ${t.type}`.toLocaleLowerCase().includes(fragment)
        )
    )),
    [p.thesis, p.search]
  );

  return (
    <>
      <Table size="sm" hover striped responsive>
        <thead>
          <tr>
            <th></th>
            <th colSpan={2}>Name</th>
            {p.search && <th>Type</th>}
            <th>Topic</th>
            <th className="text-nowrap">Who looks after?</th>
            <th className="text-nowrap">Helps with?</th>
            <th className="text-nowrap">Last meeting / email</th>
            {p.search && <th>Semester</th>}
            <th></th>
          </tr>
        </thead>
        {p.status.map(status => groupedThesis[status.id] && (
          <React.Fragment key={status.id}>
            <thead>
              <tr>
                <th
                  colSpan={p.search ? 13 : 11}
                  className="text-center fw-light text-muted"
                >
                  - {status.name} -
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedThesis[status.id].map(t =>
                <tr key={t.id} onClick={() => p.onClickThesis(t)}>
                  <td className="p-2 position-relative">
                    {t.color && t.color !== "#ffffff" && (
                      <div className="position-absolute top-0 start-0 bottom-0 end-0" style={{ background: t.color }} />
                    )}
                  </td>
                  <td>{t.salutation}</td>
                  <td className="text-nowrap">
                    <HighlightedText highlight={p.search}>{t.name}</HighlightedText>
                  </td>
                  {p.search && (
                    <td className="text-nowrap">
                      <HighlightedText highlight={p.search}>
                        {p.types.find(({ id }) => id === t.type)?.name}
                      </HighlightedText>
                    </td>
                  )}
                  <td
                    className="text-nowrap"
                    style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 }}
                  >
                    <HighlightedText highlight={p.search}>{t.topic ?? ""}</HighlightedText>
                  </td>
                  <td>
                    <HighlightedText highlight={p.search}>{t.supervisorName ?? ""}</HighlightedText>
                  </td>
                  <td>
                    <HighlightedText highlight={p.search}>{t.relatedProject ?? ""}</HighlightedText>
                  </td>
                  <td>{t.lastContactDate?.slice(0, 10)}</td>
                  {p.search && <td className="text-nowrap">
                    {p.semester.find(({ id }) => id === t.semester)?.name}
                  </td>}
                  <td>
                    {t.memo ? <CardText /> : null}
                  </td>
                </tr>
              )}
            </tbody>
          </React.Fragment>
        ))}
      </Table>
    </>
  )
};
